'use client';

import sendInquiry from '@/actions/sendInquiry';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Textarea } from '@/components/ui/textarea';
import useThankYouMessage from '@/hooks/useThankYouMessage';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { z } from 'zod';

const formSchema = z.object({
  firstname: z.string().min(1, { message: 'required_field' }),
  lastname: z.string().min(1, { message: 'required_field' }),
  message: z.string().min(1, { message: 'required_field' }),
  phone: z.string().refine(isValidPhoneNumber, { message: 'invalid_phone' }),
  email: z.string().min(1, { message: 'required_field' }).email({ message: 'invalid_email' })
});

export default function ContactForm({ data = {}, labels = {} }) {
  const { firstnameLabel, lastnameLabel, emailLabel, phoneLabel, submitLabel } = data;

  const [thankYouMessage, showThankYouMessage, hideMessage] = useThankYouMessage();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: '',
      lastname: '',
      message: '',
      phone: '',
      email: ''
    }
  });

  const { isSubmitting, errors } = form.formState;

  const getErrorMessage = useCallback(
    name => {
      return labels?.[errors?.[name]?.message] ?? errors?.[name]?.message;
    },
    [errors, labels]
  );

  async function onSubmit(values) {
    const { status, message } = await sendInquiry({
      ...values,
      page_url: location.href,
      page_title: document.title
    });

    if (status === 'success') {
      form.reset();
      showThankYouMessage('contact_us_form_thank_you_message');
    } else {
      form.setError('root', { message });
      hideMessage();
    }
  }

  return (
    <Form {...form}>
      <form
        className='contact-form bg-white rounded-lg lg:rounded-xl p-4 sm:p-6 lg:p-11 max-w-screen-lg mx-auto flex flex-col gap-4 border border-primary [&_input]:py-6'
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className='flex gap-5 flex-wrap'>
          <FormField
            name='firstname'
            control={form.control}
            render={({ field }) => (
              <FormItem className='flex-1 min-w-64 sm:min-w-72'>
                <FormControl>
                  <Input placeholder={firstnameLabel} {...field} />
                </FormControl>
                {getErrorMessage(field.name) && (
                  <p className='text-[0.8rem] font-medium text-red-400'>{getErrorMessage(field.name)}</p>
                )}
              </FormItem>
            )}
          />
          <FormField
            name='lastname'
            control={form.control}
            render={({ field }) => (
              <FormItem className='flex-1 min-w-64 sm:min-w-72'>
                <FormControl>
                  <Input placeholder={lastnameLabel} {...field} />
                </FormControl>
                {getErrorMessage(field.name) && (
                  <p className='text-[0.8rem] font-medium text-red-400'>{getErrorMessage(field.name)}</p>
                )}
              </FormItem>
            )}
          />
        </div>
        <div className='flex gap-5 flex-wrap'>
          <FormField
            name='email'
            control={form.control}
            render={({ field }) => (
              <FormItem className='flex-1 min-w-64 sm:min-w-72'>
                <FormControl>
                  <Input placeholder={emailLabel} {...field} />
                </FormControl>
                {getErrorMessage(field.name) && (
                  <p className='text-[0.8rem] font-medium text-red-400'>{getErrorMessage(field.name)}</p>
                )}
              </FormItem>
            )}
          />
          <FormField
            name='phone'
            control={form.control}
            render={({ field }) => (
              <FormItem className='flex-1 min-w-64 sm:min-w-72'>
                <FormControl>
                  <PhoneInput placeholder={phoneLabel} {...field} defaultCountry='US' />
                </FormControl>
                {getErrorMessage(field.name) && (
                  <p className='text-[0.8rem] font-medium text-red-400'>{getErrorMessage(field.name)}</p>
                )}
              </FormItem>
            )}
          />
        </div>
        <FormField
          name='message'
          control={form.control}
          render={({ field }) => (
            <FormItem className='basis-1/2'>
              <FormControl>
                <Textarea rows={10} {...field} aria-label='message' className='min-h-72 resize-none' />
              </FormControl>
              {getErrorMessage(field.name) && (
                <p className='text-[0.8rem] font-medium text-red-400'>{getErrorMessage(field.name)}</p>
              )}
              {thankYouMessage && form.formState.isSubmitSuccessful && (
                <p className='text-green-400 text-[0.8rem]'>{labels[thankYouMessage]}</p>
              )}
            </FormItem>
          )}
        />

        {errors.root && (
          <p className='general-error-message text-[0.8rem] font-medium text-destructive'>
            {labels[errors.root.message]}
          </p>
        )}

        {submitLabel && (
          <Button type='submit' size='lg' className='max-w-md w-full self-center mt-8' disabled={isSubmitting}>
            {submitLabel}
          </Button>
        )}
      </form>
    </Form>
  );
}
